/**
 * Deleting a volunteer record (Plan D12) — the one destructive action in the
 * application.
 *
 * **The panel is now shown for every volunteer, and only to an ADMIN.** Two
 * changes, in opposite directions, and both are corrections.
 *
 * It used to render nothing at all unless the record qualified. The reasoning
 * was that offering an action which will be refused teaches staff that refusals
 * are arbitrary — but hiding it taught something worse. A screen that is blank
 * where the control should be does not say "this record is protected"; it says
 * "this build is missing the feature", and the only way to find out which was to
 * ask. So the panel is always here, and where the record cannot be deleted it
 * says which of the three guards is in the way, by name and by count, and points
 * at the thing to do instead.
 *
 * It used to render for any staff member. The API has required ADMIN since D21,
 * so a TEAM account was being shown a button that could only ever answer 403.
 *
 * **The guards themselves are unchanged.** §5.8 is right about everyone it is
 * talking about: attendance is the record of work Parinaam reports on, and
 * deleting the person would take it with them. What can be deleted is still only
 * a record that never became anybody — an unactivated public submission, a
 * rejected one, or a staff entry — with no assignments and no attendance.
 *
 * The API is still the authority (§8.40): it re-checks all three inside the
 * transaction, so a record that gains attendance between this render and the
 * click is still safe. What the screen decides is only what to *offer*.
 *
 * Three deliberate frictions on the path that does delete, because it cannot be
 * undone:
 *   - it is behind a disclosure, not sitting on the page;
 *   - it asks for a reason, which the API requires and the audit row keeps;
 *   - it says plainly what will be destroyed, by name.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { VOLUNTEER_SOURCE, VOLUNTEER_STATUS } from '@parinaam-vms/shared';
import type { AssignmentDetail, AttendanceDetail, Volunteer } from '@parinaam-vms/shared';
import { Button, Card, TextArea } from '../../components/common';
import { useAuth } from '../../hooks/useAuth';
import { useDeleteVolunteer } from '../../hooks/useVolunteers';
import { formatName } from '../../utils/format';
import styles from './RemoveRecordPanel.module.css';

export interface RemoveRecordPanelProps {
  volunteer: Volunteer;
  assignments: AssignmentDetail[];
  attendance: AttendanceDetail[];
}

export function RemoveRecordPanel({ volunteer, assignments, attendance }: RemoveRecordPanelProps) {
  const { hasRole } = useAuth();

  // Hiding the panel from a TEAM account is presentation, not security — the API
  // refuses them outright. It is here so the one control that cannot be undone
  // is not on the screen of somebody who has no business using it.
  if (!hasRole('ADMIN')) return null;

  const blocked = whyBlocked(volunteer, assignments, attendance);

  return (
    <Card title="Remove this record">
      {blocked ? <Blocked reason={blocked} /> : <Deletable volunteer={volunteer} />}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Which guard, if any, is in the way
// ---------------------------------------------------------------------------

/**
 * The same three guards the API applies (D18), in the same order it reports
 * them, so the screen and the 409 never disagree about *why*.
 *
 * Status answers the question for a public submission — an INACTIVE one was
 * active once, an ACTIVE one was approved — but it cannot answer it for staff
 * entry, which lands ACTIVE without anyone deciding anything. Provenance
 * answers that.
 */
function whyBlocked(
  volunteer: Volunteer,
  assignments: AssignmentDetail[],
  attendance: AttendanceDetail[]
): { headline: string; detail: string } | null {
  if (attendance.length > 0) {
    return {
      headline:
        attendance.length === 1
          ? 'This record has 1 attendance record.'
          : `This record has ${attendance.length} attendance records.`,
      detail:
        'Hours are recorded — inactivate instead.',
    };
  }

  if (assignments.length > 0) {
    return {
      headline:
        assignments.length === 1
          ? 'This record is assigned to 1 activity.'
          : `This record is assigned to ${assignments.length} activities.`,
      detail:
        'Cancel those assignments first.',
    };
  }

  const neverBecameAnybody =
    volunteer.status === VOLUNTEER_STATUS.PENDING ||
    volunteer.status === VOLUNTEER_STATUS.REJECTED ||
    volunteer.source === VOLUNTEER_SOURCE.STAFF_ENTRY;

  if (!neverBecameAnybody) {
    return {
      headline: `${formatName(volunteer)} was approved as a volunteer, so their record is kept.`,
      detail:
        'Only an unused record can be deleted.',
    };
  }

  return null;
}

/** What the panel says when the record is protected. Never a dead button. */
function Blocked({ reason }: { reason: { headline: string; detail: string } }) {
  return (
    <div className={styles.closed}>
      <p className={styles.blocked}>
        <strong>{reason.headline}</strong> {reason.detail}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The path that actually deletes
// ---------------------------------------------------------------------------

function Deletable({ volunteer }: { volunteer: Volunteer }) {
  const navigate = useNavigate();
  const mutation = useDeleteVolunteer();

  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>();

  const handleDelete = () => {
    if (reason.trim().length < 5) {
      setError('Please give a reason of at least 5 characters.');
      return;
    }

    mutation.mutate(
      { id: volunteer.volunteer_id, reason: reason.trim() },
      { onSuccess: () => navigate('/volunteers') }
    );
  };

  if (!open) {
    return (
      <div className={styles.closed}>
        <p className={styles.explain}>
          Never used, so safe to delete.
        </p>
        <Button variant="danger" onClick={() => setOpen(true)}>
          Delete permanently
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.confirm}>
      {/*
        A staff entry has no registration behind it, so naming one would
        describe a consequence that will not happen — and on the one screen
        in the application where being precise about what is destroyed
        matters most.
      */}
      <p className={styles.warning}>
        <strong>This cannot be undone.</strong> {formatName(volunteer)} ({volunteer.volunteer_code})
        {volunteer.source === VOLUNTEER_SOURCE.PUBLIC_REGISTRATION
          ? ' and the registration that created them'
          : ''}{' '}
        will be destroyed. A record of the deletion, and your reason, stays in the audit trail.
      </p>

      <TextArea
        label="Why is this record being deleted?"
        required
        rows={2}
        value={reason}
        error={error}
        onChange={(event) => {
          setReason(event.target.value);
          setError(undefined);
        }}
      />

      {mutation.isError && (
        <p className={styles.error} role="alert">
          {(mutation.error as Error).message}
        </p>
      )}

      <div className={styles.actions}>
        <Button variant="danger" loading={mutation.isPending} onClick={handleDelete}>
          Yes, delete this record
        </Button>
        <Button
          onClick={() => {
            setOpen(false);
            setReason('');
            setError(undefined);
          }}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
